import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';

export function DiscardDialogView({ open, onSeguir, onDescartar }) {
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onSeguir(); }}>
      <DialogContent compact className="max-w-sm">
        <DialogHeader>
          <DialogTitle>¿Descartar cambios?</DialogTitle>
          <DialogDescription>
            Tienes cambios sin guardar. Si sales ahora se perderán.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onSeguir}>Seguir editando</Button>
          <Button variant="destructive" onClick={onDescartar}>Descartar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function useUnsavedGuard({ onClose }) {
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const dirtyRef = useRef(false);

  // Cada guarda necesita un nombre propio en el registro: dos modales abiertos a la vez
  // son lo normal aqui, y cerrar uno no puede dar por limpia toda la pantalla.
  const idRegistro = useId();
  const { registrarSucio, quitarSucio } = useUnsavedChanges();

  // El alta y la baja las hacen markDirty/resetDirty en el acto (abajo). Este efecto es
  // solo la red del desmontaje: un modal sucio que desaparece sin limpiar no puede
  // dejar bloqueada la aplicacion para siempre.
  useEffect(() => {
    if (!isDirty) return undefined;
    registrarSucio(idRegistro);
    return () => quitarSucio(idRegistro);
  }, [isDirty, idRegistro, registrarSucio, quitarSucio]);

  // Nada de esto prueba que el usuario haya cambiado algo:
  //
  // - Radix llama `onValueChange` de un Select cuando el valor cambia POR CODIGO, no
  //   solo cuando alguien elige (pasa por use-controllable-state), y los formularios de
  //   aqui marcan sucio dentro de ese handler.
  // - Ademas, el <select> oculto que Radix monta bajo cada Select despacha un `change`
  //   que burbujea en cuanto el valor cambia.
  //
  // Al abrir una ficha ya capturada, el efecto que la puebla dispara las dos cosas: el
  // formulario nacia sucio y Cancelar preguntaba "¿Descartar cambios?" sin que nadie
  // tecleara. Lo que separa un cambio del usuario de uno del sistema es si hubo
  // interaccion humana ANTES, dentro del formulario: abrir un Select empieza por un
  // pointerdown sobre su disparador, y teclear por un keydown.
  //
  // Por eso el hook no infiere "sucio" de un `change` a secas. `markDirty()` sigue
  // siendo la puerta explicita y SIN compuerta: CustomModal la usa para declarar que hay
  // cambios (su prop isDirty) sin que medie ningun evento del DOM, y esa palabra se
  // respeta. Un formulario que marque sucio dentro del onValueChange de un Select cae
  // en la trampa de arriba: ese handler tambien corre al poblarse.
  const huboInteraccion = useRef(false);

  const marcarInteraccion = useCallback(() => {
    huboInteraccion.current = true;
  }, []);

  // El registro se toca AQUI, no en un efecto: el bloqueador de salida lo consulta en
  // el instante de navegar, y guardar y navegar ocurren en el mismo gesto. Si la baja
  // esperara al efecto, el router preguntaria "¿Descartar cambios?" sobre un guardado
  // que ya salio bien.
  const markDirty = useCallback(() => {
    dirtyRef.current = true;
    registrarSucio(idRegistro);
    setIsDirty(true);
  }, [registrarSucio, idRegistro]);

  const resetDirty = useCallback(() => {
    dirtyRef.current = false;
    quitarSucio(idRegistro);
    setIsDirty(false);
  }, [quitarSucio, idRegistro]);

  // Salir sin guardar tiene tres caminos y este hook cubre dos. Cerrar (el boton
  // Cancelar, via onOpenChange) y recargar la pagina o cerrar la pestana, que se
  // llevaba lo tecleado sin decir nada -- y en celular eso pasa seguido. `beforeunload`
  // es lo unico que el navegador ofrece para eso.
  //
  // El tercero, navegar (el boton "atras" incluido), lo cubre BloqueoDeSalida desde que
  // la app corre sobre data router: este hook solo se anota en el registro central
  // (arriba) y aquel es el unico que bloquea. Ver el spec
  // 2026-09-10-data-router-y-guarda-de-salida-design.md.
  useEffect(() => {
    if (!isDirty) return undefined;
    const avisar = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [isDirty]);

  const onOpenChange = useCallback((open) => {
    if (open) return;
    if (dirtyRef.current) {
      setShowDiscard(true);
    } else {
      onClose?.();
    }
  }, [onClose]);

  const onChangeCapture = useCallback(() => {
    if (!huboInteraccion.current) return;
    if (!dirtyRef.current) markDirty();
  }, [markDirty]);

  const DiscardDialog = (
    <DiscardDialogView
      open={showDiscard}
      onSeguir={() => setShowDiscard(false)}
      onDescartar={() => {
        setShowDiscard(false);
        resetDirty();
        onClose?.();
      }}
    />
  );

  return {
    onOpenChange,
    dirtyProps: {
      onChangeCapture,
      onPointerDownCapture: marcarInteraccion,
      onKeyDownCapture: marcarInteraccion,
    },
    markDirty,
    resetDirty,
    isDirty,
    DiscardDialog,
  };
}
